// src/pages/DashboardPage.jsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, CircularProgress, Paper, alpha } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MedicationIcon from '@mui/icons-material/Medication';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import axiosInstance from '../axiosInstance';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Snackbar, Alert } from '@mui/material';
import { useClinic } from '../context/ClinicContext';
import ClinicDashboard from './Clinic/ClinicDashboard';
import { motion } from 'framer-motion';

// Registrar los componentes de Chart.js, incluyendo Filler
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Configuración global de Chart.js
ChartJS.defaults.font.family = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif';
ChartJS.defaults.color = '#666';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-HN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Componente mejorado para cada tarjeta de estadística
const StatCard = ({ title, value, icon, color }) => {
  const theme = useTheme();
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: { xs: 2, sm: 3 }, 
        display: 'flex', 
        alignItems: 'center', 
        height: '100%',
        width: { xs: '100%', sm: '500px' }, // Ancho responsivo
        maxWidth: '100%', // Asegura que no se desborde
        borderRadius: '16px',
        boxShadow: theme.shadows[2],
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-5px)' },
          boxShadow: theme.shadows[4]
        }
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Typography color="textSecondary" gutterBottom variant="body1">
          {title}
        </Typography>
        <Typography variant="h3" component="div" sx={{ 
          fontWeight: 'bold',
          fontSize: { xs: '1.75rem', sm: '2.5rem' }
        }}>
          {value}
        </Typography>
      </Box>
      <Box sx={{ 
        color: color,
        backgroundColor: alpha(color, 0.12),
        p: { xs: 1.5, sm: 2 },
        borderRadius: '50%'
      }}>
        {React.cloneElement(icon, { sx: { fontSize: { xs: 40, sm: 60 } } })}
      </Box>
    </Paper>
  );
};

const DashboardPage = () => {
  const { selectedClinicId } = useClinic();
  if (selectedClinicId) {
    return <ClinicDashboard />;
  }
  const theme = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();

    const fetchStats = async () => {
      try {
        const { data } = await axiosInstance.get('/dashboard-stats/', {
          signal: ctrl.signal,
          // Opcional: evita caches intermedios
          params: { t: Date.now() },
        });

        if (import.meta.env.DEV) {
          console.log('Datos recibidos del backend:', data);
          console.log('Visitas diarias:', data.visitas_diarias);
        }

        setStats(data);
        setOpenSnackbar(true);
      } catch (error) {
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          return; // ignorar cancelaciones
        }
        console.error('Error al cargar estadísticas:', error);

        if (error?.response?.status === 401) {
          toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
        } else {
          toast.error('No se pudieron cargar las estadísticas.');
        }

        // Datos de ejemplo como fallback
        setStats({
          total_pacientes: 0,
          citas_proximas: 0,
          consultas_hoy: 0,
          balance_financiero: 0,
          visitas_diarias: [0, 0, 0, 0, 0, 0, 0],
          visitas_labels: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
          distribucion_ingresos: [25, 25, 25, 25],
          categorias_ingresos: ['Consultas', 'Procedimientos', 'Medicamentos', 'Otros'],
          actividad_reciente: [],
          medicamentos_frecuentes: {
            labels: ['Paracetamol', 'Ibuprofeno', 'Amoxicilina', 'Loratadina', 'Omeprazol'],
            data: [12, 10, 8, 6, 5]
          },
          diagnosticos_frecuentes: {
            labels: ['Hipertensión', 'Diabetes', 'Infección respiratoria', 'Gastritis', 'Migraña'],
            data: [15, 12, 10, 8, 6]
          }
        });
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    return () => ctrl.abort();
  }, []);

  // Add this handler to close the Snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  if (loading || !stats) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
  }

  // Obtener el símbolo de la moneda guardada para mostrar el balance
  const currencySymbol = localStorage.getItem('currencySymbol') || '$';

  // Motion Paper para header
  const MotionPaper = motion(Paper);

  return (
    <Box 
      sx={{ 
        width: '100%', 
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 2, sm: 3 },
        boxSizing: 'border-box' 
      }}
    >
      {/* Header estilo FinanzasPage */}
      <MotionPaper
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          mb: 3,
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        {/* Burbujas decorativas */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: (t) => alpha('#fff', 0.1),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: (t) => alpha('#fff', 0.08),
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
          }}
        >
          {/* Izquierda: icono + textos */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                bgcolor: (t) => alpha('#fff', 0.2),
                color: 'white',
                borderRadius: '50%',
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (t) => `0 8px 32px ${alpha('#000', 0.2)}`,
                flexShrink: 0,
              }}
            >
              <DashboardIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}
              >
                Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {(() => {
                  const genero = user?.medico?.genero; // 'Masculino' | 'Femenino' | undefined
                  const prefijo = genero === 'Femenino' ? 'Dra.' : 'Dr.';
                  const nombre = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
                  // Fallbacks si no hay datos
                  const nombreMostrar = nombre || 'Médico';
                   return ` Bienvenido de nuevo, ${prefijo} ${nombreMostrar}`;
                  })()} 
              </Typography>
            </Box>
          </Box>

          {/* Derecha: texto adicional si quieres añadir acciones futuras */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            {/* Espacio reservado para futuros botones de acción del dashboard */}
          </Box>
        </Box>
      </MotionPaper>

      {/* Tarjetas de estadísticas mejoradas */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ width: '100%', mx: 0 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Total de Pacientes" 
            value={stats.total_pacientes}
            icon={<PeopleIcon />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Próximas Citas (7 días)" 
            value={stats.citas_proximas}
            icon={<EventIcon />}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Consultas de Hoy" 
            value={stats.consultas_hoy}
            icon={<MedicalServicesIcon />}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard 
            title="Balance Financiero" 
            value={`${currencySymbol} ${formatCurrency(stats.balance_financiero)}`}
            icon={<AccountBalanceWalletIcon />}
            color={stats.balance_financiero >= 0 ? theme.palette.success.main : theme.palette.error.main}
          />
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 2 }, width: '100%', mx: 0 }}>
        {/* Gráfico de visitas de pacientes */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 3 },
              height: '100%',
              width: { xs: '100%', md: '1250px' }, // Ancho responsivo
              maxWidth: '100%', // Asegura que no se desborde
              borderRadius: '16px',
              boxShadow: theme.shadows[2]
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Visitas de Pacientes (Últimos 7 días)
            </Typography>
            <Box sx={{ height: { xs: 250, sm: 300 }, width: '100%' }}>
              <Line 
                data={{
                  labels: stats.visitas_labels || ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'],
                  datasets: [{
                    label: 'Pacientes',
                    data: stats.visitas_diarias || [0, 0, 0, 0, 0, 0, 0],
                    borderColor: theme.palette.primary.main,
                    tension: 0.1,
                    fill: true,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1)
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      labels: {
                        boxWidth: 12,
                        font: {
                          size: 11
                        }
                      }
                    },
                    title: {
                      display: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        font: {
                          size: 10
                        }
                      }
                    },
                    x: {
                      ticks: {
                        font: {
                          size: 10
                        },
                        maxRotation: 45,
                        minRotation: 45
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Gráfico de distribución de ingresos */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 4 },
              height: '100%',
              width: { xs: '100%', md: '800px' }, // Ancho responsivo
              maxWidth: '100%', // Asegura que no se desborde
              borderRadius: '16px',
              boxShadow: theme.shadows[2]
            }}
          >
            <Typography 
              variant="h6" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Distribución de Ingresos
            </Typography>
            <Box sx={{ 
              height: { xs: 220, sm: 300 },
              width: '100%', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center' 
            }}>
              <Doughnut 
                data={{
                  labels: stats.categorias_ingresos || ['Consultas', 'Procedimientos', 'Medicamentos', 'Otros'],
                  datasets: [{
                    data: stats.distribucion_ingresos || [25, 25, 25, 25],
                    backgroundColor: [
                      alpha(theme.palette.primary.main, 0.7),
                      alpha(theme.palette.secondary.main, 0.7),
                      alpha(theme.palette.info.main, 0.7),
                      alpha(theme.palette.warning.main, 0.7)
                    ],
                    borderColor: [
                      theme.palette.primary.main,
                      theme.palette.secondary.main,
                      theme.palette.info.main,
                      theme.palette.warning.main
                    ],
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        boxWidth: 12,
                        font: {
                          size: 10
                        },
                        padding: 10
                      }
                    }
                  }
                }}
              />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Medicamentos y Diagnósticos */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 2, sm: 4 }, width: '100%', mx: 0 }}>
        {/* Medicamentos más utilizados */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 4 },
              height: '100%',
              width: { xs: '100%', md: '1050px' }, // Ancho responsivo
              maxWidth: '100%', // Asegura que no se desborde
              borderRadius: '16px',
              boxShadow: theme.shadows[2]
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MedicationIcon sx={{ 
                fontSize: { xs: 20, sm: 24 },
                mr: 1, 
                color: theme.palette.info.main,
                padding: 0.5,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.info.main, 0.1)
              }} />
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  mb: 0, 
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Medicamentos más utilizados
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 250, sm: 300 }, width: '100%' }}>
              {stats.medicamentos_frecuentes && 
              stats.medicamentos_frecuentes.labels && 
              stats.medicamentos_frecuentes.labels.length > 0 ? (
                <Bar
                  data={{
                    labels: stats.medicamentos_frecuentes.labels,
                    datasets: [
                      {
                        label: 'Frecuencia de prescripción',
                        data: stats.medicamentos_frecuentes.data,
                        backgroundColor: alpha(theme.palette.info.main, 0.7),
                        borderColor: theme.palette.info.main,
                        borderWidth: 1,
                        borderRadius: 6
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `Prescrito ${context.raw} veces`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Número de prescripciones',
                          font: {
                            size: 10
                          }
                        },
                        beginAtZero: true,
                        ticks: {
                          font: {
                            size: 10
                          }
                        }
                      },
                      y: {
                        ticks: {
                          font: {
                            size: 10
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                  <Typography color="textSecondary">
                    No hay datos suficientes
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        
        {/* Principales diagnósticos */}
        <Grid item xs={12} md={6}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 4 },
              height: '100%',
              width: { xs: '100%', md: '1000px' }, // Ancho responsivo
              maxWidth: '100%', // Asegura que no se desborde
              borderRadius: '16px',
              boxShadow: theme.shadows[2]
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <HealthAndSafetyIcon sx={{ 
                fontSize: { xs: 20, sm: 24 },
                mr: 1, 
                color: theme.palette.secondary.main,
                padding: 0.5,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.secondary.main, 0.1)
              }} />
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  mb: 0, 
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Principales diagnósticos
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 250, sm: 300 }, width: '100%' }}>
              {stats.diagnosticos_frecuentes && 
              stats.diagnosticos_frecuentes.labels && 
              stats.diagnosticos_frecuentes.labels.length > 0 ? (
                <Bar
                  data={{
                    labels: stats.diagnosticos_frecuentes.labels,
                    datasets: [
                      {
                        label: 'Frecuencia de diagnóstico',
                        data: stats.diagnosticos_frecuentes.data,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.7),
                        borderColor: theme.palette.secondary.main,
                        borderWidth: 1,
                        borderRadius: 6
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `Diagnosticado ${context.raw} veces`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Número de casos',
                          font: {
                            size: 10
                          }
                        },
                        beginAtZero: true,
                        ticks: {
                          font: {
                            size: 10
                          }
                        }
                      },
                      y: {
                        ticks: {
                          font: {
                            size: 10
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                  <Typography color="textSecondary">
                    No hay datos suficientes
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Sección de actividad reciente */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 2 }, width: '100%', mx: 0 }}>
        <Grid item xs={12}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 4 },
              width: { xs: '100%', sm: '500px' }, // Ancho responsivo
              maxWidth: '100%', // Asegura que no se desborde
              borderRadius: '16px',
              boxShadow: theme.shadows[2]
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EventIcon sx={{ 
                fontSize: { xs: 20, sm: 24 },
                mr: 1, 
                color: theme.palette.primary.main,
                padding: 0.5,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }} />
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  mb: 0, 
                  fontWeight: 'bold',
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Actividad Reciente
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 300, overflow: 'auto', width: '100%' }}>
              {(stats.actividad_reciente && stats.actividad_reciente.length > 0 ? stats.actividad_reciente : [
                { id: 1, tipo: 'cita', paciente: 'Juan Pérez', fecha: '2025-08-01 10:00' },
                { id: 2, tipo: 'pago', paciente: 'María López', monto: 1500, fecha: '2025-08-01 09:30' },
                { id: 3, tipo: 'consulta', paciente: 'Carlos Ruiz', fecha: '2025-07-31 16:45' }
              ]).map(actividad => (
                <Box key={actividad.id} sx={{ 
                  p: { xs: 1.5, sm: 2 },
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '8px',
                  width: '100%',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }
                }}>
                  <Box sx={{
                    mr: { xs: 1, sm: 2 },
                    p: { xs: 0.75, sm: 1 },
                    borderRadius: '50%',
                    backgroundColor: actividad.tipo === 'cita' 
                      ? alpha(theme.palette.secondary.main, 0.1)
                      : actividad.tipo === 'pago'
                        ? alpha(theme.palette.success.main, 0.1)
                        : alpha(theme.palette.info.main, 0.1)
                  }}>
                    {actividad.tipo === 'cita' && <EventIcon sx={{ 
                      color: theme.palette.secondary.main,
                      fontSize: { xs: 18, sm: 24 }
                    }} />}
                    {actividad.tipo === 'pago' && <AccountBalanceWalletIcon sx={{ 
                      color: theme.palette.success.main,
                      fontSize: { xs: 18, sm: 24 }
                    }} />}
                    {actividad.tipo === 'consulta' && <MedicalServicesIcon sx={{ 
                      color: theme.palette.info.main,
                      fontSize: { xs: 18, sm: 24 }
                    }} />}
                  </Box>
                  <Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 'medium',
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {actividad.paciente}
                      {actividad.monto && ` - ${currencySymbol} ${formatCurrency(actividad.monto)}`}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="textSecondary"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {actividad.fecha}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={8000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="info" 
          variant="filled"
          sx={{ 
            width: '100%',
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          Para cambiar el tipo de moneda mostrado en el Balance Financiero ingresa al módulo de Finanzas
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardPage;
